/**
 * Web Vitals Hook
 * Monitors Core Web Vitals (LCP, FID, CLS, INP)
 */

import { useEffect } from 'react';
import type { Metric } from 'web-vitals';

export interface WebVitalsMetric {
  name: 'CLS' | 'FCP' | 'FID' | 'INP' | 'LCP' | 'TTFB';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

interface UseWebVitalsOptions {
  onMetric?: (metric: WebVitalsMetric) => void;
  reportToAnalytics?: boolean;
}

/**
 * Convert web-vitals Metric to our WebVitalsMetric format
 */
const convertMetric = (metric: Metric): WebVitalsMetric => {
  const rating = getRating(metric.name as any, metric.value);
  return {
    name: metric.name as any,
    value: metric.value,
    rating,
    delta: metric.delta,
    id: metric.id
  };
};

/**
 * Hook to measure and report Web Vitals
 */
export const useWebVitals = (options: UseWebVitalsOptions = {}) => {
  const { onMetric, reportToAnalytics = true } = options;

  useEffect(() => {
    const reportMetric = (metric: Metric) => {
      const convertedMetric = convertMetric(metric);
      // Call custom callback
      if (onMetric) {
        onMetric(convertedMetric);
      }

      // Report to analytics if enabled
      if (reportToAnalytics && typeof window !== 'undefined') {
        // Google Analytics 4
        if ('gtag' in window) {
          (window as any).gtag('event', convertedMetric.name, {
            value: Math.round(convertedMetric.value),
            metric_rating: convertedMetric.rating,
            metric_delta: Math.round(convertedMetric.delta),
            metric_id: convertedMetric.id,
          });
        }

        // PostHog (if available)
        if ('posthog' in window && (window as any).posthog?.capture) {
          (window as any).posthog.capture('web_vital', {
            metric_name: convertedMetric.name,
            metric_value: Math.round(convertedMetric.value),
            metric_rating: convertedMetric.rating,
          });
        }
      }

      // Log in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Web Vitals] ${convertedMetric.name}:`, {
          value: Math.round(convertedMetric.value),
          rating: convertedMetric.rating,
        });
      }
    };

    // Dynamically import web-vitals
    import('web-vitals').then((webVitals) => {
      webVitals.getCLS(reportMetric);
      webVitals.getFCP(reportMetric);
      webVitals.getFID(reportMetric);
      webVitals.getLCP(reportMetric);
      webVitals.getTTFB(reportMetric);
      // INP might not be available in all versions
      if ('getINP' in webVitals && typeof webVitals.getINP === 'function') {
        (webVitals as any).getINP(reportMetric);
      }
    }).catch(error => {
      console.error('Failed to load web-vitals:', error);
    });
  }, [onMetric, reportToAnalytics]);
};

/**
 * Get rating for a metric value
 */
export const getRating = (
  metricName: WebVitalsMetric['name'],
  value: number
): 'good' | 'needs-improvement' | 'poor' => {
  const thresholds = {
    CLS: { good: 0.1, poor: 0.25 },
    FCP: { good: 1800, poor: 3000 },
    FID: { good: 100, poor: 300 },
    INP: { good: 200, poor: 500 },
    LCP: { good: 2500, poor: 4000 },
    TTFB: { good: 800, poor: 1800 },
  };

  const { good, poor } = thresholds[metricName];

  if (value <= good) return 'good';
  if (value <= poor) return 'needs-improvement';
  return 'poor';
};

export default useWebVitals;

